import { InfoOutlineIcon, PlusOutlineIcon } from '@carboncopyco/icons';
import {
  Button,
  Checkbox,
  ColorHex,
  Paper,
  TextBase,
  TextMenuHeader,
  Tooltip,
  Typography,
} from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRef } from 'react';
import { usePlaybookModalState } from '../PlaybookModalContext';
import { PLAYBOOK_ASSET_LIMIT } from '../consts';
import { AssetList } from './AssetList';
import VendorIcons from './VendorIcons';

export const PlaybookPanel = () => {
  const flags = useFlags();
  const { assets, multiTab, toggleMultiTab } = usePlaybookModalState();
  const addUrlRef = useRef();
  const [showAddUrl, setShowAddUrl] = useBoolean();
  const maxAssetsSelected = assets.length >= PLAYBOOK_ASSET_LIMIT;
  const multiTabCheckboxRef = useRef<HTMLDivElement>();

  return (
    <Paper
      radius
      border="ds-regular"
    >
      <div className="h-full bg-ds-natural-50 rounded-lg">
        <div className="w-[380px] flex flex-col">
          <div className="flex items-center justify-between p-5">
            <TextMenuHeader label="playbook" />
            <div ref={addUrlRef}>
              <Button
                blue
                link
                leftIcon={<PlusOutlineIcon />}
                label="Add URL"
                onClick={setShowAddUrl.setTrue}
                disabled={showAddUrl || maxAssetsSelected}
              />
            </div>
            <Tooltip
              trigger="mouseenter"
              noArrow
              placement="top"
              anchorRef={addUrlRef}
              delay={[350, undefined]}
            >
              <div className="flex flex-col gap-2 p-3">
                {maxAssetsSelected ? (
                  <Typography
                    fontSize="text-ds-200"
                    lineHeight="leading-ds-200"
                    color="text-ds-natural"
                    fontWeight="font-ds-normal"
                    letterSpacing="tracking-ds-tight"
                    label={
                      <>
                        You’ve reached the <br />
                        maximum of 20 assets <br />
                        per Playbook.
                      </>
                    }
                  />
                ) : (
                  <>
                    <Typography
                      fontSize="text-ds-200"
                      lineHeight="leading-ds-200"
                      color="text-ds-natural"
                      fontWeight="font-ds-normal"
                      letterSpacing="tracking-ds-tight"
                      label={
                        <>
                          Incorporate web pages <br /> to enrich the narrative.
                        </>
                      }
                    />
                    <VendorIcons />
                  </>
                )}
              </div>
            </Tooltip>
          </div>
          <AssetList
            showAddUrl={showAddUrl}
            onCloseAddUrl={setShowAddUrl.setFalse}
          />
          {flags.multiTabDisplay && (
            <div className="border-t border-ds-natural-150 p-4">
              <div className="flex items-center gap-1">
                <Checkbox
                  name="multiTabDisplay"
                  checked={multiTab}
                  onChange={toggleMultiTab}
                  label={<TextBase label="Present each asset in a new tab" />}
                />
                <div ref={multiTabCheckboxRef}>
                  <InfoOutlineIcon
                    size={12}
                    color={ColorHex.natural300}
                  />
                </div>
                <Tooltip
                  trigger="mouseenter"
                  delay={[350, undefined]}
                  placement="top"
                  anchorRef={multiTabCheckboxRef}
                >
                  <Typography
                    fontSize="text-ds-200"
                    lineHeight="leading-ds-200"
                    color="text-ds-natural"
                    fontWeight="font-ds-normal"
                    letterSpacing="tracking-ds-tight"
                    label={
                      <div className="p-3">
                        This setting is relevant only when <br />
                        presenting using the desktop app.
                      </div>
                    }
                  />
                </Tooltip>
              </div>
            </div>
          )}
        </div>
      </div>
    </Paper>
  );
};
